// Freier Text der Schnellerfassung: zerlegen, zeigen, nichts verschlucken
// (ST-PTY-001, CM-CAP-001, CM-PTY-001..002, CM-UX-001..002, NFR-UX-003..005,
// NFR-ACC-001).
//
// WELCHE ZUSAGE HIER EINGELOEST WIRD, woertlich aus CM-CAP-001: "Ein
// universelles Quick-Capture-Feld muss Name, E-Mail, Telefon, Firma ODER FREIEN
// TEXT entgegennehmen und einen strukturierten Party-Entwurf erzeugen." Und aus
// der Akzeptanz: "Vor Speicherung werden erkannte Felder [und] Party-Typ
// gezeigt; unbekannte Angaben verhindern die Anlage nicht."
//
// Bis zu diesem Slice fuehrte der Bildschirm ausschliesslich Einzelfelder --
// Vorname, Nachname, Firmenname, E-Mail, Telefon. Das Wort "freien Text" der
// Anforderung war damit nachweisbar nicht gebaut, und `docs/agent/handoff.md`
// fuehrt genau das als Grund, warum `CM-CAP-001` auf `planned` steht.
//
// WAS DIESER SLICE AUSDRUECKLICH NICHT BAUT -- und beides bleibt der Grund,
// warum `CM-CAP-001` weiterhin auf `planned` steht:
//
//   * KEINE DUBLETTENANZEIGE. Die dritte Akzeptanzhaelfte ("moegliche Dubletten
//     werden gezeigt") braucht einen Leseweg ueber bestehende Parteien. Ein
//     `GET /parties` zoege eine Leseregel fuer Fachdaten nach sich und waechst
//     in Richtung `ST-SEA-001`; die Story fuehrt die Dublettenpruefung als
//     ausdrueckliches Nichtziel, `CM-CAP-005` traegt `ST-DQ-001`.
//   * KEINE EIGENE QUELLE FUER DEN FREITEXT. "Quelle wird gespeichert" ist zur
//     Haelfte erfuellt und bleibt es: `apps/api` schreibt
//     `QUICK_CAPTURE_SOURCE_SYSTEM = 'liaison_ui'` fest in
//     `party.contact_point.source_system`, und zwar unabhaengig davon, wie die
//     Angabe auf den Bildschirm kam. Der Eingang "aus freiem Text uebernommen"
//     waere ein ZWEITER Wert -- die Route nimmt kein Quellenfeld entgegen, die
//     Spalte traegt heute genau einen Wert je Slice, und der Kopf von
//     quick-capture.ts in apps/api stellt jeden weiteren Wert ausdruecklich
//     unter den Vorbehalt von Product Council und Data Owner. Das waere eine
//     Aenderung an apps/api und am Vokabular, nicht an diesem Bildschirm.
//
// ---------------------------------------------------------------------------
// DIE TRAGENDE ZUSAGE: "unbekannte Angaben verhindern die Anlage nicht"
// ---------------------------------------------------------------------------
//
// Sie ist die schwerste der drei, weil sie zwei Dinge gleichzeitig verlangt,
// die sich bequem gegeneinander ausspielen lassen:
//
//   1. Was sich nicht zuordnen laesst, darf die Anlage NICHT blockieren. Kein
//      Fehler, keine Warnung, kein deaktivierter Knopf.
//   2. Was sich nicht zuordnen laesst, darf auch nicht STILL VERSCHWINDEN. Ein
//      Parser, der den Rest wegwirft, erfuellt Punkt 1 muehelos -- und macht
//      aus einer Zusage eine Falle: die Nutzerin hat eine Notiz eingefuegt,
//      sieht einen angelegten Kontakt und erfaehrt nie, dass die Haelfte ihrer
//      Eingabe nirgends steht.
//
// Deshalb liefert `parseFreeText` NICHT nur Felder, sondern eine vollstaendige
// Zerlegung: was erkannt wurde, welcher Party-Typ daraus folgt, UND jedes
// Bruchstueck, das uebrig blieb, mit dem Grund, warum. Der Rest ist kein
// Nebenprodukt, sondern Rueckgabewert -- ein Aufrufer kann ihn nicht
// uebersehen, ohne es zu wollen.
//
// Kapitel 5.2 Nr. 3 ("Capture now, enrich safely: ein Minimum genuegt zur
// Anlage; Herkunft und Unsicherheit bleiben SICHTBAR") und Nr. 5
// ("Source-aware") verlangen genau diese Sichtbarkeit; Nr. 8 ("Human control:
// AI-Vorschlaege sind erklaerbar, editierbar, ablehnbar") gilt sinngemaess auch
// fuer eine regelbasierte Erkennung: sie ist ein VORSCHLAG, kein Ergebnis.
// Korrigierbar ist sie, weil jedes erkannte Feld in dem Eingabefeld landet, das
// es ohnehin traegt -- die Korrektur braucht keine zweite Oberflaeche.
//
// ---------------------------------------------------------------------------
// WARUM REGELN UND KEIN MODELL
// ---------------------------------------------------------------------------
//
// Der naheliegende Griff waere ein Sprachmodell. Er ist hier falsch, und zwar
// aus vier Gruenden, die nichts mit Geschmack zu tun haben: er braucht einen
// Netzweg und einen Anbieter (ADR-0019 verlangt fuer jedes Fremdpaket und jeden
// Anbieter einen benannten Eintrag), er schickt personenbezogene Daten an einen
// Dritten (CM-SEC/Datenschutz, und die Signatur einer Person ist genau das), er
// ist nicht deterministisch pruefbar, und er kostet Wartezeit an der Stelle,
// an der `CM-CAP-002` zehn Sekunden zusagt. Regeln sind schwaecher und dafuer
// erklaerbar, sofort, offline und in jeder Probe reproduzierbar.
//
// DIE REGELN SIND ABSICHTLICH ZURUECKHALTEND. Wo eine Regel unsicher ist,
// erkennt sie NICHTS und legt das Bruchstueck in den Rest. Das ist die
// billigere Fehlerrichtung: ein nicht erkanntes Feld kostet einen Tastendruck,
// ein falsch erkanntes kostet einen falschen Datensatz, den niemand mehr
// nachprueft. Dieselbe Abwaegung wie in next-improvement.ts, wo ein erfundener
// Vorschlag ausdruecklich schlechter ist als keiner.

import { fieldLabelFor, UNKNOWN_FIELD_LABEL, type PartyKind, type QuickCaptureForm } from './quick-capture.ts';

/** Die Felder, die aus freiem Text entstehen koennen. */
export type RecognizedFieldCode = 'givenName' | 'familyName' | 'legalName' | 'email' | 'phone';

/**
 * Warum ein Bruchstueck nicht uebernommen wurde.
 *
 * Der Grund ist Teil der Zusage und nicht Zierde. "Nicht uebernommen" allein
 * laesst die Nutzerin raten, ob die Anwendung es nicht verstanden hat oder ob
 * sie es verstanden und verworfen hat -- und das sind zwei voellig verschiedene
 * naechste Schritte (NFR-UX-005).
 */
export type UnassignedReason =
  /** Keine Regel hat gegriffen. */
  | 'not_recognized'
  /** Verstanden, aber das Feld ist bereits aus einem frueheren Bruchstueck belegt. */
  | 'already_captured'
  /** Verstanden, gehoert aber zu einer Firma -- der Entwurf ist eine Person. */
  | 'not_for_person'
  /** Verstanden, gehoert aber zu einer Person -- der Entwurf ist eine Firma. */
  | 'not_for_organization'
  /** Die Funktion unter dem Namen; gespeichert wird sie nur an "Arbeitet für" (S1b). */
  | 'function_without_field'
  /** Die Funktion bei gewaehlter Organisation, ohne Code im Feld "Funktion". */
  | 'function_without_code'
  /** Eine Angabe der gewaehlten, schon vorhandenen Organisation; die Anlage schreibt dort nichts (S2, Vorgabe §1). */
  | 'belongs_to_organisation';

export interface RecognizedField {
  readonly code: RecognizedFieldCode;
  readonly value: string;
}

export interface UnassignedFragment {
  /** Der Wortlaut, wie er im Freitext stand. Nie eine Zusammenfassung. */
  readonly text: string;
  readonly reason: UnassignedReason;
}

export interface FreeTextDraft {
  /** Der Party-Typ, der aus dem Erkannten folgt -- oder der vorgegebene. */
  readonly kind: PartyKind;
  /** In fester Anzeigereihenfolge, nicht in Fundreihenfolge. */
  readonly recognized: readonly RecognizedField[];
  /** In Fundreihenfolge -- die Nutzerin liest ihren eigenen Text von oben. */
  readonly unassigned: readonly UnassignedFragment[];
}

export const EMPTY_DRAFT: FreeTextDraft = Object.freeze({
  kind: 'person',
  recognized: Object.freeze([]),
  unassigned: Object.freeze([]),
});

/**
 * Der Feldpfad der API zu jedem erkannten Feld.
 *
 * ER IST DER GRUND, WARUM ES HIER KEINE ZWEITE FELDNAMENLISTE GIBT. Die
 * deutschen Feldnamen stehen in `FIELD_LABELS` (quick-capture.ts) und sind dort
 * gegen den Katalog der Route geprueft. Eine zweite Liste haette denselben
 * Zweck und liefe auseinander, ohne dass es auffaellt -- dieselbe Familie wie
 * die abgeschriebenen Grundcodes, die access.test.ts seit jeher verhindert.
 */
export const REQUEST_FIELD_PATH_OF: Readonly<Record<RecognizedFieldCode, string>> = Object.freeze({
  givenName: 'person.givenName',
  familyName: 'person.familyName',
  legalName: 'organization.legalName',
  email: 'email',
  phone: 'phone',
});

/** Der deutsche Feldname eines erkannten Feldes -- aus derselben Quelle wie das Formular. */
export function recognizedFieldLabel(code: RecognizedFieldCode): string {
  return fieldLabelFor(REQUEST_FIELD_PATH_OF[code]);
}

/** Der Party-Typ in Fachsprache. "Party" kommt auf dem Bildschirm nicht vor (NFR-UX-003). */
export const PARTY_KIND_LABELS: Readonly<Record<PartyKind, string>> = Object.freeze({
  person: 'Person',
  organization: 'Organisation',
});

/**
 * Die Gruende in Fachsprache.
 *
 * Jeder Satz nennt, WAS mit dem Bruchstueck ist -- und keiner davon nennt einen
 * Fehler, denn keiner ist einer. Das ist die sprachliche Seite der Zusage:
 * unbekannte Angaben verhindern die Anlage nicht, also klingen sie auch nicht
 * so.
 */
export const UNASSIGNED_REASON_TEXTS: Readonly<Record<UnassignedReason, string>> = Object.freeze({
  not_recognized: 'lässt sich keinem Feld dieses Bildschirms zuordnen',
  already_captured: 'ist eine zweite Angabe derselben Art, übernommen ist die erste',
  not_for_person: 'gehört zu einer Organisation, dieser Entwurf ist eine Person',
  not_for_organization: 'gehört zu einer Person, dieser Entwurf ist eine Organisation',
  function_without_field: 'ist die Funktion, gespeichert wird sie nur mit einer Organisation unter „Arbeitet für“',
  function_without_code: 'ist die Funktion, wähle unter „Funktion“ selbst, was passt',
  // Der Bildschirm nennt die Organisation beim Namen (organisationsGrund); dies ist der Satz ohne Namen.
  belongs_to_organisation: 'gehört zur Organisation unter „Arbeitet für“, pflege es dort'
});

/**
 * Der Satz, der die Zusage von CM-CAP-001 auf dem Bildschirm ausspricht.
 *
 * Er steht dort AUCH DANN, wenn nichts uebrig blieb -- nein, ausdruecklich
 * nicht: er erscheint nur mit dem Rest, denn ein Satz ueber etwas, das es nicht
 * gibt, ist Laerm (Kapitel 5.2 Nr. 14).
 */
export const UNASSIGNED_IS_NOT_A_BLOCKER =
  'Diese Angaben halten die Anlage nicht auf. Sie werden nicht mitgespeichert.';

// ---------------------------------------------------------------------------
// Grenzen
// ---------------------------------------------------------------------------

/**
 * Was aus freiem Text hoechstens als Name uebernommen wird.
 *
 * Der Wert ist NICHT frei gewaehlt: `MAXIMUM_NAME_LENGTH` der Route ist 200,
 * und ein Freitextfund, der laenger waere, koennte von der Route nur abgelehnt
 * werden. Ein Parser, der eine Ablehnung erzeugt, hat nichts erkannt -- er hat
 * eine Sackgasse gebaut. free-text.test.ts haelt diese Zahl gegen apps/api,
 * damit sie nicht auseinanderlaufen.
 */
export const MAXIMUM_RECOGNIZED_NAME_LENGTH = 200;
export const MAXIMUM_RECOGNIZED_EMAIL_LENGTH = 254;
export const MAXIMUM_RECOGNIZED_PHONE_LENGTH = 32;

/**
 * Ziffern, ab denen ein Zahlenlauf als Rufnummer gilt.
 *
 * STRENGER ALS DIE ROUTE, und das ist der Punkt. Die Route laesst vier Ziffern
 * zu, weil dort jemand ausdruecklich eine Nummer in das Telefonfeld getippt hat
 * -- die Absicht ist bekannt. In freiem Text ist ein vierstelliger Zahlenlauf
 * weitaus haeufiger eine Jahreszahl, eine Hausnummer oder eine Postleitzahl als
 * eine Rufnummer. Die Obergrenze ist dagegen dieselbe wie in der Route
 * (E.164: hoechstens 15 Ziffern), damit dieser Bildschirm der Route nie einen
 * Wert uebergibt, den sie ablehnen muss.
 */
const MINIMUM_PHONE_DIGITS_IN_FREE_TEXT = 6;
const MAXIMUM_PHONE_DIGITS = 15;

// ---------------------------------------------------------------------------
// Die Regeln
// ---------------------------------------------------------------------------

/**
 * Wo ein Bruchstueck endet.
 *
 * Zeilenumbruch, Semikolon, senkrechter Strich, Tabulator -- die Trenner, die in
 * Signaturen, Visitenkartenabschriften und Notizen tatsaechlich vorkommen. DAS
 * KOMMA IST BEWUSST KEIN TRENNER: "Meier, Anna" ist eine Namensschreibweise und
 * kein Aufzaehlungszeichen. Einzeiler mit Kommas werden trotzdem zerlegt, aber
 * ueber die Ausschnitte weiter unten (E-Mail und Rufnummer werden aus dem
 * Bruchstueck HERAUSGELOEST) und nicht ueber einen Trenner, der eine
 * Schreibweise zerreisst.
 */
// Dieselben Trenner wie address-paste.ts (LINE_SEPARATOR), mit X5: auch ·, • und „ – “.
const FRAGMENT_SEPARATOR = /[\n\r;|\t·•]+|\s+–\s+/u;

/** Woerter, mit denen eine Registerangabe beginnt; was so anfaengt, ist kein Personenname. */
const REGISTER_PHRASE = /^(Amtsgericht|Registergericht|Handelsregister|Registernummer|Sitz|Steuernummer|USt-IdNr|UID)(?![\p{L}\p{N}])/iu;

/**
 * Die Form einer Beschriftung am Zeilenanfang: "Tel.: ", "E-Mail: ".
 *
 * Nach dem Doppelpunkt muss ein Leerzeichen stehen oder das Bruchstueck enden.
 * Ohne diese Bedingung fiele "https://beispiel.test" darunter -- das Schema
 * einer Adresse ist keine Beschriftung.
 */
const LEADING_LABEL = /^([\p{L}][\p{L}\p{M}.\-/ ]{0,23}):(?:[ \t]+|$)/u;

/**
 * Welche Beschriftungen ueberhaupt welche sind.
 *
 * DAS IST DER TEIL, DER NICHT ZU VERALLGEMEINERN WAR, und der Anlass ist
 * belegt: die erste Fassung entfernte JEDES Wort vor einem Doppelpunkt. Damit
 * verschwand "Lieblingsthema:" aus "Lieblingsthema: skandinavische Krimis" --
 * und aus der Notiz einer Nutzerin wurde ein Rest, dem der halbe Sinn fehlte.
 * Die Probe "Die Zerlegung wirft nichts weg" hat genau das gefunden.
 *
 * Eine Beschriftung darf nur wegfallen, wenn sie ein Feld DIESES Bildschirms
 * benennt -- dann steht ihr Inhalt danach im Feld und nichts geht verloren.
 * Jedes andere Wort vor einem Doppelpunkt ist eine Angabe und bleibt.
 *
 * "Fax" steht bewusst NICHT hier: eine Faxnummer hat auf diesem Bildschirm
 * kein Feld, und die ganze Zeile gehoert deshalb in den Rest.
 */
const KNOWN_LABELS: ReadonlySet<string> = new Set([
  'tel', 'telefon', 'telefonnummer', 'telefonnr', 'mobil', 'mobiltelefon', 'handy', 'festnetz', 'durchwahl',
  'email', 'mail', 'emailadresse', 'mailadresse',
  'firma', 'unternehmen', 'firmenname',
  'name', 'vorname', 'nachname',
]);

/** Die Beschriftungen, nach denen ein Zahlenlauf auch ohne Vorwahlform eine Rufnummer ist. */
const PHONE_LABELS: ReadonlySet<string> = new Set([
  'tel', 'telefon', 'telefonnummer', 'telefonnr', 'mobil', 'mobiltelefon', 'handy', 'festnetz', 'durchwahl',
]);

function leadingLabel(fragment: string): string | undefined {
  const match = LEADING_LABEL.exec(fragment);
  return match === null ? undefined : (match[1] as string).replace(/[.\s-]/gu, '').toLowerCase();
}

/** Entfernt eine bekannte Beschriftung am Zeilenanfang -- und nur eine bekannte. */
function stripKnownLabel(fragment: string): string {
  const match = LEADING_LABEL.exec(fragment);
  if (match === null) {
    return fragment;
  }
  const normalized = leadingLabel(fragment) as string;
  return KNOWN_LABELS.has(normalized) ? fragment.slice(match[0].length) : fragment;
}

/**
 * Das Linkziel, das ein Mailprogramm beim Kopieren vor den sichtbaren Text setzt:
 * "[http://+49511...]+49511..." oder "[tel:+49 511 ...]+49 511 ...". Steht derselbe
 * Wert direkt dahinter, faellt das Ziel als Wiederholung weg; sonst bleibt sein
 * Inhalt ohne Klammern und Schema stehen, damit keine Angabe verloren geht.
 * "tel:" und "mailto:" direkt vor dem Wert sind Schreibweise, keine Angabe.
 */
const LINK_TARGET = /\[(?:https?:\/\/|tel:|mailto:)([^\]]{0,254})\]/giu;
const LINK_SCHEME = /\b(?:tel|mailto):(?=\S)/giu;

function withoutLinkNotation(fragment: string): string {
  const compact = (value: string): string => value.replace(/\s+/gu, '').toLowerCase();
  return fragment
    .replace(LINK_TARGET, (_match, target: string, offset: number, whole: string) => {
      const after = compact(whole.slice(offset + _match.length));
      const inner = target.trim();
      return inner.length > 0 && after.startsWith(compact(inner)) ? ' ' : ` ${inner} `;
    })
    .replace(LINK_SCHEME, '')
    .replace(/\s{2,}/gu, ' ')
    .trim();
}

const EMAIL = /[\p{L}\p{N}._%+'-]+@[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)*\.[\p{L}]{2,}/u;

/** Ein Zahlenlauf mit Trennzeichen, wie er in Signaturen steht. */
const PHONE = /(?:\+|00)?\d[\d\s().\/-]{3,}\d/gu;

/**
 * Die Form einer Rufnummer ohne Beschriftung: Landes- oder Ortsvorwahl vorn.
 * Ohne sie waere jede Registernummer ("HRB 123456", "DE 123456789") eine
 * Rufnummer; mit Beschriftung genuegt der Zahlenlauf.
 */
const PHONE_SHAPE = /^(?:\+|\(?0)/u;

/** Uhrzeit oder Zeitspanne ("09.00-14.00", "8:30 - 16:45"): nie eine Rufnummer. */
const TIME_OR_SPAN = /^\d{1,2}[.:]\d{2}(?:\s*[-–]\s*\d{1,2}[.:]\d{2})?$/u;

/**
 * Erreichbarkeit statt Nummer: eine Wochentagsspanne ("Mo. - Fr."), ein
 * ausgeschriebener Wochentag oder "Uhr" im Bruchstueck. Dann ist ein Zahlenlauf
 * darin eine Zeit, auch hinter "Telefon:". Ein einzelnes "So" ist ein Satzanfang.
 */
const OPENING_HOURS =
  /\b(?:Mo|Di|Mi|Do|Fr|Sa|So)\.?\s*[-–]\s*(?:Mo|Di|Mi|Do|Fr|Sa|So)\b|\b(?:Montag|Dienstag|Mittwoch|Donnerstag|Freitag|Samstag|Sonntag|Uhr)\b/u;

/**
 * Ein Fax ist KEIN Kontaktweg dieses Slice.
 *
 * `party.contact_point.channel` kennt in diesem Slice 'email' und 'phone'
 * (QUICK_CAPTURE_CHANNELS in apps/api). Eine Faxnummer als Telefonnummer zu
 * uebernehmen waere kein Erkennungsfehler, sondern eine falsche Zusage: das
 * Feld hiesse "Telefon", der Wert waere keins, und niemand wuerde es je
 * bemerken. Sie geht deshalb ausdruecklich in den Rest.
 */
const FAX_LABEL = /\b(?:fax|telefax)\b/iu;

/**
 * Rechtsformen und die zwei branchenueblichen Namensbestandteile.
 *
 * ZWEI LISTEN UND NICHT EINE, und der Grund ist eine Fehlerklasse: die kurzen
 * Formen sind in Kleinschreibung gewoehnliche Woerter. "5 kg Papier" wuerde
 * unter einer durchgaengig case-insensitiven Suche zur Firma "5 kg". Die
 * mehrbuchstabigen Formen sind dagegen eindeutig genug, um jede Schreibweise
 * zu erlauben -- "gmbh" am Zeilenende ist keine Verwechslung wert.
 */
const LEGAL_FORM_ANY_CASE =
  /(?:\bgmbh\s*&\s*co\.?\s*kg\b|\bgmbh\b|\bmbh\b|\bkgaa\b|\bohg\b|\bgbr\b|\bltd\b\.?|\binc\b\.?|\bllc\b|\bplc\b|\bverlagsgruppe\b|\bverlag\b)/giu;
const LEGAL_FORM_EXACT_CASE = /(?:\bAG\b|\bKG\b|\bUG\b|\beG\b|e\.\s?V\.|S\.A\.|B\.V\.|N\.V\.)/gu;

/**
 * Namenszusaetze, die zum Nachnamen gehoeren und klein geschrieben sind.
 *
 * Ohne sie waere "Anna von Meier" ein Vorname "Anna von" und ein Nachname
 * "Meier" -- oder, schlimmer, gar kein Name, weil ein kleingeschriebenes Wort
 * die Namenspruefung reissen laesst.
 */
const NAME_PARTICLES: ReadonlySet<string> = new Set([
  'von', 'vom', 'van', 'de', 'del', 'della', 'di', 'da', 'du', 'den', 'der', 'ten', 'ter', 'zu', 'zum', 'la', 'le',
]);

/** Anreden, die kein Namensbestandteil sind. */
const SALUTATIONS: ReadonlySet<string> = new Set(['herr', 'herrn', 'frau', 'hr', 'fr']);

function digitCount(value: string): number {
  return (value.match(/\d/gu) ?? []).length;
}

/** Fuehrende und abschliessende Satzzeichen, die aus einer Zerlegung uebrig bleiben. */
function trimPunctuation(value: string): string {
  return value.replace(/^[\s,;:.–—-]+/u, '').replace(/[\s,;:–—-]+$/u, '').trim();
}

interface StrippedTitles {
  readonly value: string;
  /** Was dabei wegfiel -- und was deshalb im Rest wieder auftauchen muss. */
  readonly dropped: readonly string[];
}

/**
 * Titel und Anreden am Rand eines Bruchstuecks.
 *
 * Die Regel ist bewusst formal statt aufzaehlend: ein fuehrendes oder
 * abschliessendes Wort, das einen Punkt enthaelt, ist ein Titel oder ein
 * Abschluss -- "Dr.", "Prof.", "Dipl.-Ing.", "h.c.", "M.A.". Eine Liste
 * konkreter Titel waere am Tag ihrer Entstehung unvollstaendig; die Form
 * traegt weiter. Anreden tragen keinen Punkt und stehen deshalb als kurze
 * Liste daneben.
 *
 * WARUM DAS ABGETRENNTE ZURUECKGEGEBEN WIRD UND NICHT VERFAELLT: ein Titel ist
 * eine ANGABE ueber die Person und kein Formatierungszeichen. Dieser
 * Bildschirm hat kein Feld dafuer -- also ist er genau das, was CM-CAP-001
 * "unbekannte Angaben" nennt, und er gehoert sichtbar in den Rest. Ihn hier
 * still fallen zu lassen waere derselbe Fehler wie ein weggeworfener
 * Notizsatz, nur unauffaelliger.
 *
 * Die Beschriftung am Zeilenanfang ("Tel.: ", "E-Mail: ") wird dagegen
 * ausdruecklich NICHT gemeldet: sie ist ein Feldname und keine Angabe. Was sie
 * benennt, steht danach im Feld selbst.
 */
function stripTitles(value: string): StrippedTitles {
  let tokens = value.split(/\s+/u).filter((token) => token.length > 0);
  const dropped: string[] = [];
  const isTitle = (token: string): boolean =>
    token.includes('.') || SALUTATIONS.has(token.replace(/[.]/gu, '').toLowerCase());
  while (tokens.length > 0 && isTitle(tokens[0] as string)) {
    dropped.push(tokens[0] as string);
    tokens = tokens.slice(1);
  }
  while (tokens.length > 0 && isTitle(tokens[tokens.length - 1] as string)) {
    dropped.push(tokens[tokens.length - 1] as string);
    tokens = tokens.slice(0, -1);
  }
  return { value: tokens.join(' '), dropped };
}

function isNameToken(token: string): boolean {
  if (!/^[\p{L}][\p{L}\p{M}'’-]*$/u.test(token)) {
    return false;
  }
  // Klein geschrieben ist nur zulaessig, was ein Namenszusatz ist. Er darf auch
  // ganz vorn stehen -- "von Meier" ist ein Nachname ohne Vornamen, und die
  // erste Fassung dieser Zeile liess ihn deshalb komplett durchfallen.
  return /^\p{Lu}/u.test(token) || NAME_PARTICLES.has(token.toLowerCase());
}

/**
 * Die Woerter eines Bruchstuecks, wenn ALLE davon Namensbestandteile sein
 * koennen -- und mindestens eines gross geschrieben ist.
 *
 * Die zweite Bedingung ist die Gegenprobe zur ersten: ohne sie waeren "van der"
 * oder "de la" fuer sich genommen ein Name.
 */
function nameTokens(value: string): readonly string[] | undefined {
  const tokens = value.split(/\s+/u).filter((token) => token.length > 0);
  if (tokens.length === 0 || value.length > MAXIMUM_RECOGNIZED_NAME_LENGTH) {
    return undefined;
  }
  if (!tokens.some((token) => /^\p{Lu}/u.test(token))) {
    return undefined;
  }
  return tokens.every((token) => isNameToken(token)) ? tokens : undefined;
}

interface PersonName {
  readonly givenName: string;
  readonly familyName: string;
  /** Titel und Anreden, die zum Namen standen und kein Feld haben. */
  readonly dropped: readonly string[];
}

/**
 * Ein Personenname aus einem bereinigten Bruchstueck, oder keiner.
 *
 * ZWEI WOERTER SIND DAS MINIMUM. Ein einzelnes grossgeschriebenes Wort ist
 * mehrdeutig -- "Bonnier" ist eine Firma, "Anna" ein Vorname, "Hamburg" ein
 * Ort -- und die Regel hat keine Grundlage, sich zu entscheiden. Sie entscheidet
 * deshalb nicht: das Wort geht in den Rest, bleibt sichtbar und kostet einen
 * Tastendruck. Ein geratener halber Name kostet einen falschen Datensatz.
 *
 * VIER WOERTER SIND DAS MAXIMUM. Darueber ist es in aller Regel kein Name,
 * sondern ein Satz -- und ein Satz im Namensfeld ist genau der Fall, den die
 * Laengengrenze der Route als "in das falsche Feld kopierter Absatz"
 * beschreibt.
 */
function personNameFrom(value: string): PersonName | undefined {
  // Zusaetze in Klammern ("(she/her)", "(sie/ihr)", "(Elternzeit)") sind kein
  // Namensteil. Wie ein Titel haben sie kein Feld und gehen sichtbar in den Rest.
  const brackets = value.match(/\([^()]*\)/gu) ?? [];
  const withoutBrackets = value.replace(/\s*\([^()]*\)\s*/gu, ' ');
  const { value: cleaned, dropped: titles } = stripTitles(trimPunctuation(withoutBrackets));
  const dropped = [...titles, ...brackets];
  if (cleaned.length === 0) {
    return undefined;
  }

  // "Meier, Anna" -- Nachname zuerst. Eine gaengige Schreibweise, und ohne
  // diese Zeile waere sie ein Vorname "Meier" und ein Nachname "Anna", also
  // gerade vertauscht. Erst NACH stripTitles geprueft, sonst risse ein
  // "Anna Meier, M.A." hier faelschlich in zwei Namen auseinander.
  const swapped = /^([^,]+),\s*([^,]+)$/u.exec(cleaned);
  if (swapped !== null) {
    const family = nameTokens((swapped[1] as string).trim());
    const given = nameTokens((swapped[2] as string).trim());
    if (family !== undefined && given !== undefined && family.length <= 3 && given.length <= 2) {
      return { givenName: given.join(' '), familyName: family.join(' '), dropped };
    }
    return undefined;
  }

  const tokens = nameTokens(cleaned);
  if (tokens === undefined || tokens.length < 2 || tokens.length > 4) {
    return undefined;
  }

  // Ein Namenszusatz als erstes Wort heisst: der ganze Fund ist der Nachname.
  // "von Meier" hat keinen Vornamen, und "von" als Vorname waere schlechter als
  // ein fehlender.
  if (NAME_PARTICLES.has((tokens[0] as string).toLowerCase())) {
    return { givenName: '', familyName: tokens.join(' '), dropped };
  }

  let cut = tokens.length - 1;
  while (cut > 1 && NAME_PARTICLES.has((tokens[cut - 1] as string).toLowerCase())) {
    cut -= 1;
  }
  return { givenName: tokens.slice(0, cut).join(' '), familyName: tokens.slice(cut).join(' '), dropped };
}

/**
 * Ein Personenname aus einem Stueck ausserhalb der Felder der Person -- die
 * Leitung einer Organisation (Signatur S3). Dieselbe Regel wie fuer die Person
 * selbst, damit "Dr. Max Muster" hier wie dort "Max" und "Muster" ergibt; die
 * Titel fallen weg, weil der Entwurf der Leitung nur den Namen traegt.
 */
export function recognizedPersonName(value: string): { readonly givenName: string; readonly familyName: string } | undefined {
  const name = personNameFrom(value);
  return name === undefined ? undefined : { givenName: name.givenName, familyName: name.familyName };
}

interface LegalNameFind {
  readonly legalName: string;
  /** Was hinter der Rechtsform noch stand -- meist ein Ort, und der gehoert nicht hierher. */
  readonly rest: string;
}

/**
 * Ein Firmenname, erkannt an seiner Rechtsform.
 *
 * DER SCHNITT LIEGT HINTER DER RECHTSFORM UND NICHT AM ZEILENENDE. "Bonnier
 * Media Deutschland GmbH, Hamburg" ist ein Firmenname und ein Ort; das Ganze
 * als Firmennamen zu speichern waere ein Wert, den niemand je wieder sauber
 * bekommt. Der Ort geht in den Rest und bleibt sichtbar. Seit "Anschrift
 * einfuegen" (29.09.2026) laeuft VOR dieser Zerlegung address-paste.ts: eine
 * erkannte Anschrift ist dann schon aus dem Text genommen, und was hier
 * ankommt, ist der Rest. Anschriften erkennt diese Datei weiterhin nicht.
 */
function legalNameFrom(value: string): LegalNameFind | undefined {
  let end: number | undefined;
  for (const pattern of [LEGAL_FORM_ANY_CASE, LEGAL_FORM_EXACT_CASE]) {
    pattern.lastIndex = 0;
    for (let match = pattern.exec(value); match !== null; match = pattern.exec(value)) {
      const candidate = match.index + match[0].length;
      if (end === undefined || candidate > end) {
        end = candidate;
      }
    }
  }
  if (end === undefined) {
    return undefined;
  }
  const legalName = trimPunctuation(value.slice(0, end));
  if (legalName.length === 0 || legalName.length > MAXIMUM_RECOGNIZED_NAME_LENGTH) {
    return undefined;
  }
  return { legalName, rest: trimPunctuation(value.slice(end)) };
}

// ---------------------------------------------------------------------------
// Die Zerlegung
// ---------------------------------------------------------------------------

interface Candidate {
  readonly code: RecognizedFieldCode;
  readonly value: string;
}

/** Ein Fund oder ein Rest, in der Reihenfolge, in der er im Text stand. */
type Finding =
  | { readonly hit: Candidate; readonly extra?: undefined; readonly reason?: undefined }
  | { readonly hit?: undefined; readonly extra: string; readonly reason?: UnassignedReason };

/**
 * Die Zeile direkt unter dem ersten Namen ist die Funktion, wenn sie nur aus
 * Woertern besteht: keine Ziffer, kein @, keine Beschriftung, keine Rechtsform,
 * hoechstens acht Woerter. "Customer Success Management" saehe sonst wie ein
 * zweiter Name aus.
 */
const FUNCTION_LINE = /^[\p{L}][\p{L}\p{M}&/.,'’ -]*$/u;
const MAXIMUM_FUNCTION_WORDS = 8;

/**
 * Alle Funde und Reste eines einzelnen Bruchstuecks, in Lesereihenfolge.
 *
 * Die Reihenfolge der Ausschnitte ist nicht beliebig: E-Mail zuerst, weil eine
 * Adresse Ziffern enthalten kann und sonst als halbe Rufnummer verschwaende;
 * dann die Rufnummer, weil sie sich aus einem Namen nicht herausloesen laesst;
 * was dann noch dasteht, ist Name oder Firma oder Rest.
 */
function findingsIn(fragment: string): readonly Finding[] {
  const findings: Finding[] = [];
  // Gegen das URSPRUENGLICHE Bruchstueck geprueft und nicht gegen den Rest:
  // stuende die Beschriftung erst einmal nicht mehr da, waere aus "Fax: +49 40
  // 7654321" eine Telefonnummer geworden. Genau so ist es in der ersten Fassung
  // passiert.
  const isFax = FAX_LABEL.test(fragment);
  const plain = withoutLinkNotation(fragment);
  const label = leadingLabel(plain);
  const hasPhoneLabel = label !== undefined && PHONE_LABELS.has(label);
  let remainder = stripKnownLabel(plain);

  const email = EMAIL.exec(remainder);
  if (email !== null && email[0].length <= MAXIMUM_RECOGNIZED_EMAIL_LENGTH) {
    findings.push({ hit: { code: 'email', value: email[0] } });
    remainder = `${remainder.slice(0, email.index)} ${remainder.slice(email.index + email[0].length)}`;
  }

  if (!isFax && !OPENING_HOURS.test(remainder)) {
    for (const phone of remainder.matchAll(PHONE)) {
      const value = trimPunctuation(phone[0]);
      const digits = digitCount(value);
      if (
        digits >= MINIMUM_PHONE_DIGITS_IN_FREE_TEXT &&
        digits <= MAXIMUM_PHONE_DIGITS &&
        value.length <= MAXIMUM_RECOGNIZED_PHONE_LENGTH &&
        !TIME_OR_SPAN.test(value) &&
        (hasPhoneLabel || PHONE_SHAPE.test(value))
      ) {
        findings.push({ hit: { code: 'phone', value } });
        remainder = `${remainder.slice(0, phone.index)} ${remainder.slice((phone.index as number) + phone[0].length)}`;
        break;
      }
    }
  }

  remainder = trimPunctuation(stripKnownLabel(remainder.trim()));
  if (remainder.length === 0) {
    return findings;
  }

  const organization = legalNameFrom(remainder);
  if (organization !== undefined) {
    findings.push({ hit: { code: 'legalName', value: organization.legalName } });
    if (organization.rest.length > 0) {
      findings.push({ extra: organization.rest });
    }
    return findings;
  }

  const person = personNameFrom(remainder);
  if (person !== undefined) {
    if (person.givenName.length > 0) {
      findings.push({ hit: { code: 'givenName', value: person.givenName } });
    }
    findings.push({ hit: { code: 'familyName', value: person.familyName } });
    for (const title of person.dropped) {
      findings.push({ extra: title });
    }
    return findings;
  }

  findings.push({ extra: remainder });
  return findings;
}

/** Die Anzeigereihenfolge der erkannten Felder -- fest, nicht die Fundreihenfolge. */
export const RECOGNIZED_FIELD_ORDER: readonly RecognizedFieldCode[] = Object.freeze([
  'givenName',
  'familyName',
  'legalName',
  'email',
  'phone',
]);

function reasonForKindMismatch(code: RecognizedFieldCode, kind: PartyKind): UnassignedReason | undefined {
  if (kind === 'person' && code === 'legalName') {
    return 'not_for_person';
  }
  if (kind === 'organization' && (code === 'givenName' || code === 'familyName')) {
    return 'not_for_organization';
  }
  return undefined;
}

/**
 * Zerlegt freien Text in einen strukturierten Entwurf.
 *
 * `kindOverride` ist die Entscheidung der NUTZERIN, nicht der Regeln. Ohne sie
 * folgt der Party-Typ dem Fund: ein Personenname macht eine Person, sonst macht
 * ein Firmenname eine Firma, sonst bleibt es bei der Voreinstellung des
 * Formulars. Mit ihr gilt sie -- und was dann nicht zum gewaehlten Typ passt,
 * verschwindet NICHT, sondern erscheint mit dem Grund im Rest.
 *
 * DAS IST DER TEURE TEIL DIESER SIGNATUR, und er ist Absicht. Ohne den
 * Parameter waere der Fall "Nutzerin waehlt Firma, fuegt eine Personensignatur
 * ein" ein stiller Verlust: der Vorname stuende in einem Feld, das bei Art
 * 'organization' gar nicht sichtbar ist, und `buildRequestBody` liesse ihn
 * weg -- ohne dass irgendwo etwas davon stuende. Genau die Bauart, die
 * CM-CAP-001 mit "unbekannte Angaben verhindern die Anlage nicht" gerade nicht
 * meint.
 */
export function parseFreeText(text: string, kindOverride?: PartyKind): FreeTextDraft {
  if (text.trim().length === 0) {
    return kindOverride === undefined ? EMPTY_DRAFT : { ...EMPTY_DRAFT, kind: kindOverride };
  }

  const findings: Finding[] = [];
  let nameSeen = false;
  let previousWasName = false;
  for (const fragment of text.split(FRAGMENT_SEPARATOR)) {
    const trimmed = fragment.trim();
    if (trimmed.length === 0) {
      continue;
    }
    if (
      previousWasName &&
      FUNCTION_LINE.test(trimmed) &&
      trimmed.split(/\s+/u).length <= MAXIMUM_FUNCTION_WORDS &&
      legalNameFrom(trimmed) === undefined
    ) {
      findings.push({ extra: trimmed, reason: 'function_without_field' });
      previousWasName = false;
      continue;
    }
    // Registerangaben sind nie ein Name, auch wenn sie wie einer aussehen ("Amtsgericht
    // Hannover" aus "HRB 123456 • Amtsgericht Hannover", seit X5 als eigenes Stueck): sonst
    // wuerde eine Organisationssignatur zur Person (Codex-P2 #758).
    if (REGISTER_PHRASE.test(trimmed)) {
      findings.push({ extra: trimmed, reason: 'not_recognized' });
      previousWasName = false;
      continue;
    }
    const found = findingsIn(trimmed);
    const isName = found.some((finding) => finding.hit?.code === 'familyName');
    // Eine zweite Zeile, die nur wie ein Name aussieht ("Amtsgericht Hannover", seit X5 als
    // eigene Zeile), bleibt EIN Stueck im Rest und zerfaellt nicht in Vor- und Nachname; als
    // Name gilt nur der erste.
    if (isName && nameSeen && found.every((finding) => finding.hit?.code === 'givenName' || finding.hit?.code === 'familyName')) {
      findings.push({ extra: trimmed, reason: 'not_recognized' });
      previousWasName = false;
      continue;
    }
    previousWasName = isName && !nameSeen;
    nameSeen = nameSeen || isName;
    findings.push(...found);
  }

  const hits = findings.flatMap((finding) => (finding.hit === undefined ? [] : [finding.hit]));
  const hasPersonName = hits.some((hit) => hit.code === 'givenName' || hit.code === 'familyName');
  const hasLegalName = hits.some((hit) => hit.code === 'legalName');
  const kind: PartyKind =
    kindOverride ?? (hasPersonName ? 'person' : hasLegalName ? 'organization' : EMPTY_DRAFT.kind);

  const taken = new Map<RecognizedFieldCode, string>();
  const unassigned: UnassignedFragment[] = [];
  for (const finding of findings) {
    if (finding.hit === undefined) {
      unassigned.push({ text: finding.extra, reason: finding.reason ?? 'not_recognized' });
      continue;
    }
    const mismatch = reasonForKindMismatch(finding.hit.code, kind);
    if (mismatch !== undefined) {
      unassigned.push({ text: finding.hit.value, reason: mismatch });
      continue;
    }
    if (taken.has(finding.hit.code)) {
      unassigned.push({ text: finding.hit.value, reason: 'already_captured' });
      continue;
    }
    taken.set(finding.hit.code, finding.hit.value);
  }

  const recognized = RECOGNIZED_FIELD_ORDER.flatMap((code) => {
    const value = taken.get(code);
    return value === undefined ? [] : [{ code, value }];
  });

  return { kind, recognized, unassigned };
}

// ---------------------------------------------------------------------------
// Der Weg in das Formular
// ---------------------------------------------------------------------------

/** Was die Nutzerin selbst angefasst hat und was der Freitext deshalb nicht mehr anfasst. */
export type OverridableField = RecognizedFieldCode | 'kind';

/**
 * Traegt den Entwurf in das Formular ein.
 *
 * ER SCHREIBT NUR, WAS DIE NUTZERIN NICHT SELBST ANGEFASST HAT. Ohne diese
 * Regel waere "korrigierbar" eine Falle: wer den erkannten Nachnamen berichtigt
 * und danach noch eine Zeile in den Freitext schreibt, saehe seine Korrektur
 * verschwinden. Ein Bildschirm, der Eingaben zurueckdreht, wird nach dem ersten
 * Mal nicht mehr benutzt (NFR-UX-005).
 *
 * Und er schreibt AUCH DAS LEERE FELD: wird der Freitext geleert, verschwindet,
 * was aus ihm entstand. Alles andere hinterliesse Werte ohne Herkunft.
 */
export function applyFreeTextDraft(
  draft: FreeTextDraft,
  base: QuickCaptureForm,
  overridden: ReadonlySet<OverridableField>,
): QuickCaptureForm {
  const recognizedValue = (code: RecognizedFieldCode): string =>
    draft.recognized.find((field) => field.code === code)?.value ?? '';
  const choose = (code: RecognizedFieldCode, current: string): string =>
    overridden.has(code) ? current : recognizedValue(code);

  return {
    kind: overridden.has('kind') ? base.kind : draft.kind,
    givenName: choose('givenName', base.givenName),
    familyName: choose('familyName', base.familyName),
    legalName: choose('legalName', base.legalName),
    email: choose('email', base.email),
    phone: choose('phone', base.phone),
  };
}

// ---------------------------------------------------------------------------
// Was der Bildschirm sagt
// ---------------------------------------------------------------------------

/** Beschriftungen des Erkennungsblocks. An einer Stelle, damit die Probe sie halten kann. */
export const FREE_TEXT_LABEL = 'Signatur oder Visitenkarte einfügen (optional)';
/**
 * Der Hinweis unter dem Feld.
 *
 * KURZ, denn er steht bei JEDEM Besuch da -- auch bei dem, der die Felder
 * einzeln ausfuellt und den Freitext nie benutzt. Die erste Fassung war ein
 * ganzer Absatz mit Aufzaehlung und Korrekturzusage; der Experience-Review vom
 * 29.08.2026 hat sie als dauerhaft sichtbares Beiwerk verworfen (Kapitel 5.2
 * Nr. 9). Was der Satz leisten muss, ist genau zweierlei: sagen, was hinein
 * darf, und sagen, wohin es geht.
 */
export const FREE_TEXT_HINT = 'Erkannte Angaben stehen danach in den Feldern darunter.';
/** Beispiel im leeren Feld (v4): sagt, was hinein darf, ohne den Hinweis zu verlaengern. */
export const FREE_TEXT_PLACEHOLDER = 'Zum Beispiel die Signatur aus einer E-Mail: Name, Organisation, Telefon, E-Mail, Anschrift.';
export const RECOGNITION_HEADING = 'Aus dem eingefügten Text erkannt';
export const NOTHING_RECOGNIZED = 'Aus diesem Text ließ sich noch nichts zuordnen.';
export const UNASSIGNED_HEADING = 'Nicht zugeordnet';

/** Die Zeile ueber der Disclosure: was nicht uebernommen wurde, im Wortlaut der Nutzerin. */
export function unassignedLine(fragments: readonly UnassignedFragment[]): string {
  return `Nicht übernommen: ${fragments.map((fragment) => `„${fragment.text}“`).join(', ')}`;
}

/**
 * Der Organisationsname aus einer Personensignatur. Er passt nicht in den Entwurf einer
 * Person und steht deshalb im Rest; "Arbeitet fuer" bietet ihn als Suchbegriff an.
 * Zugeordnet wird erst, was die Nutzerin aus den Treffern waehlt.
 */
export function organisationFromDraft(draft: FreeTextDraft): string | undefined {
  return draft.unassigned.find((fragment) => fragment.reason === 'not_for_person')?.text;
}

/**
 * Die erkannte Funktion im Wortlaut. Sie bleibt im Rest, bis die Schnellerfassung
 * eine Funktion an "Arbeitet für" annimmt; dann ist sie der Vorschlag fuer den
 * Code aus dem Rollenkatalog.
 */
export function functionFromDraft(draft: FreeTextDraft): string | undefined {
  return draft.unassigned.find((fragment) => fragment.reason === 'function_without_field')?.text;
}

/**
 * Die Funktion im Rest, sobald "Arbeitet für" eine Organisation nennt (S1b): mit einem
 * Code im Feld "Funktion" ist sie uebernommen und faellt weg, ohne Code bleibt sie mit
 * dem Satz, der auf das Feld zeigt. Ohne Organisation bleibt alles, wie es ist.
 */
export function withTakenFunction(unassigned: readonly UnassignedFragment[], organisation: boolean, roleCode: string | undefined): readonly UnassignedFragment[] {
  if (!organisation) return unassigned;
  return unassigned.flatMap((fragment) =>
    fragment.reason !== 'function_without_field' ? [fragment] : roleCode === undefined ? [{ ...fragment, reason: 'function_without_code' as const }] : [],
  );
}

/**
 * Die Bruchstuecke ohne den Organisationsnamen, wenn er als "Arbeitet für"
 * uebernommen ist (gewaehlt aus seiner Suche oder neu angelegt). Sonst stuende
 * er als "nicht uebernommen" neben der Beziehung, die ihn gerade festhaelt.
 */
export function withoutTakenOrganisation(unassigned: readonly UnassignedFragment[], taken: string | undefined): readonly UnassignedFragment[] {
  if (taken === undefined) return unassigned;
  const index = unassigned.findIndex((fragment) => fragment.reason === 'not_for_person' && fragment.text.trim() === taken.trim());
  return index < 0 ? unassigned : [...unassigned.slice(0, index), ...unassigned.slice(index + 1)];
}

/**
 * Die Zusammenfassung fuer die Vorlesestimme.
 *
 * WARUM NUR EINE ZUSAMMENFASSUNG UND NICHT DIE LISTE: der Block aendert sich
 * bei JEDEM Tastendruck im Freitextfeld. Eine Vorleseregion, die dabei ihren
 * ganzen Inhalt neu ansagt, ist unbenutzbar -- sie redet dauernd und sagt
 * nichts. Dieser Satz aendert sich dagegen nur, wenn sich die ERKENNUNG
 * aendert; solange der Text nur waechst, ohne dass ein neues Feld entsteht,
 * bleibt er Wort fuer Wort derselbe und wird nicht erneut angesagt. Die
 * Bruchstuecke selbst stehen unmittelbar darunter als Liste unter einer
 * Ueberschrift und sind damit anspringbar (NFR-ACC-001, CM-UX-002).
 */
export function recognitionSummary(draft: FreeTextDraft): string {
  if (draft.recognized.length === 0 && draft.unassigned.length === 0) {
    return '';
  }
  const parts: string[] = [];
  parts.push(
    draft.recognized.length === 0
      ? NOTHING_RECOGNIZED
      : `Erkannt: ${draft.recognized.map((field) => recognizedFieldLabel(field.code)).join(', ')}.`,
  );
  if (draft.recognized.length > 0) {
    // Der Party-Typ steht seit dem Experience-Review vom 29.08.2026 NUR hier
    // und nicht zusaetzlich als eigene Zeile: eine zweite Anzeige derselben
    // Angabe waere dieselbe doppelte Wahrheit wie die verworfene Werteliste.
    // Sichtbar ist er ausserdem an der gewaehlten Art des Kontakts darunter --
    // dort aber nur als Zustand eines Bedienelements, und ein Zustand wird
    // nicht vorgelesen, wenn ihn niemand angefasst hat (NFR-ACC-001).
    parts.push(`Art des Kontakts: ${PARTY_KIND_LABELS[draft.kind]}.`);
  }
  if (draft.unassigned.length > 0) {
    parts.push(
      draft.unassigned.length === 1
        ? 'Nicht zugeordnet: eine Angabe.'
        : `Nicht zugeordnet: ${draft.unassigned.length} Angaben.`,
    );
  }
  return parts.join(' ');
}

/** Wie viele Bruchstuecke die Bestaetigung nach der Anlage einzeln nennt. */
const CARRIED_LIMIT = 3;

/**
 * Was die Bestaetigung ueber das sagt, was NICHT mitgespeichert wurde.
 *
 * WARUM DAS IN DIE BESTAETIGUNG GEHOERT UND NICHT NUR DAVOR: nach dem 201 wird
 * das Formular geleert, damit die naechste Erfassung sofort beginnen kann. Ohne
 * diesen Satz waere genau in diesem Augenblick der Rest verschwunden -- die
 * Nutzerin saehe eine Erfolgsmeldung und haette keine Gelegenheit mehr zu
 * bemerken, dass ein Teil ihrer Eingabe nirgends steht. Das ist die zweite
 * Haelfte von "unbekannte Angaben verhindern die Anlage nicht": sie verhindern
 * sie nicht, und sie verschwinden dabei auch nicht unbemerkt.
 *
 * HOECHSTENS DREI EINZELN. Eine vollstaendige Liste waere ein Mangelbericht
 * neben einer Erfolgsmeldung und stuende gegen Kapitel 5.2 Nr. 14; die Zahl
 * dahinter haelt die Aussage trotzdem vollstaendig.
 */
export function carriedOverText(unassigned: readonly UnassignedFragment[]): string | undefined {
  if (unassigned.length === 0) {
    return undefined;
  }
  const shown = unassigned.slice(0, CARRIED_LIMIT).map((fragment) => `„${fragment.text}“`);
  const remaining = unassigned.length - shown.length;
  const list = remaining > 0 ? `${shown.join(', ')} und ${remaining} weitere` : shown.join(', ');
  // "aus dem Freitext" ist nicht schmueckend: ohne diese drei Worte spricht der
  // Satz scheinbar ueber den Kontakt und widerspricht damit dem Satz daneben,
  // der ueber die Felder des Kontakts spricht (Experience-Review 29.08.2026).
  return `Aus dem Freitext nicht mitgespeichert: ${list}. Für diese Angaben gibt es hier noch kein Feld.`;
}

/**
 * Wahr, wenn jeder erkannte Feldname aus dem Katalog der Route stammt.
 *
 * Sie steht hier und nicht nur in der Probe, weil sie eine Zusage ueber den
 * ANZEIGETEXT ist: ein erkanntes Feld, dessen Name auf den Rueckfall "eine
 * Angabe" faellt, waere auf dem Bildschirm eine Auskunft, mit der niemand
 * etwas anfangen kann (NFR-UX-003).
 */
export function everyRecognizedFieldIsNamed(): boolean {
  return RECOGNIZED_FIELD_ORDER.every((code) => recognizedFieldLabel(code) !== UNKNOWN_FIELD_LABEL);
}
